import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Check, X, RefreshCw, ClipboardCheck } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const STEP_LABELS = {
  profile_complete: 'Profile',
  api_key_issued: 'API Key',
  webhook_verified: 'Webhook',
  catalog_received: 'Catalog',
  payout_connected: 'Payout',
};

function StepPill({ label, ok, testId }) {
  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
        ok ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
      }`}
      data-testid={testId}
    >
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      {label}
    </div>
  );
}

export default function AdminDrlpOnboarding({ user, onLogout }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/drlp/onboarding-status');
      setRows(res.data.drlps || []);
    } catch (e) {
      toast.error(`Failed to load onboarding status: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fullyLive = rows.filter((r) => r.percent === 100).length;

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="drlp-onboarding-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="drlp-onboarding-title">
              <ClipboardCheck className="w-7 h-7 text-teal-600" />
              DRLP Onboarding Checklist
            </h1>
            <p className="text-gray-500 mt-1">
              Track each pilot retailer across the 5 go-live steps: profile, API key, webhook, catalog, payout.
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="drlp-onboarding-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card><CardContent className="pt-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total DRLPs</p>
            <p className="text-2xl font-bold mt-1" data-testid="drlp-onboarding-total">{rows.length}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Fully Ready (100%)</p>
            <p className="text-2xl font-bold mt-1 text-emerald-600" data-testid="drlp-onboarding-ready">{fullyLive}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider">In Progress</p>
            <p className="text-2xl font-bold mt-1 text-amber-600">{rows.length - fullyLive}</p>
          </CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Retailers</CardTitle>
            <CardDescription>Sorted by completion. Green = done, grey = pending.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-gray-500 py-8 text-center">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="text-gray-500 py-8 text-center" data-testid="drlp-onboarding-empty">No DRLPs found.</p>
            ) : (
              <div className="space-y-3">
                {rows.map((r) => (
                  <div
                    key={r.drlp_id}
                    className="flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-lg border p-4"
                    data-testid={`drlp-row-${r.drlp_id}`}
                  >
                    <div className="min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{r.name}</p>
                        <Badge variant={r.store_status === 'live' ? 'default' : 'secondary'} className="text-xs">
                          {r.store_status || 'unknown'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">{r.email}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(STEP_LABELS).map((k) => (
                        <StepPill
                          key={k}
                          label={STEP_LABELS[k]}
                          ok={r.steps[k]}
                          testId={`drlp-step-${r.drlp_id}-${k}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full ${r.percent === 100 ? 'bg-emerald-500' : 'bg-teal-500'}`}
                          style={{ width: `${r.percent}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium tabular-nums" data-testid={`drlp-percent-${r.drlp_id}`}>
                        {r.percent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
